import React from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { ScreenshotTimelineEntry } from '../../types/ScreenshotTypes';
import { screenshotCaption } from './screenshotUtils';

interface ScreenshotStripProps {
  entries: ScreenshotTimelineEntry[];
  selectedId: number;
  onSelect: (id: number) => void;
  /** Total captures the service holds, ignoring the page size. */
  total: number;
  showingAll: boolean;
  onShowAll: () => void;
}

const THUMB_WIDTH = 96;

/**
 * Horizontal history of a domain's captures, newest first. Clicking a
 * thumbnail selects which capture the main image / modal show. Rendered only
 * when there is more than one capture (WHEEL-394).
 */
const ScreenshotStrip = ({
  entries,
  selectedId,
  onSelect,
  total,
  showingAll,
  onShowAll,
}: ScreenshotStripProps) => {
  const hasMore = total > entries.length;

  return (
    <Box data-testid="screenshot-strip">
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          pb: 0.5,
          // Keep the selected border visible when it sits at the edge.
          px: '2px',
        }}
      >
        {entries.map(entry => {
          const selected = entry.id === selectedId;
          return (
            <Tooltip key={entry.id} title={screenshotCaption(entry)}>
              <Box
                component="button"
                type="button"
                onClick={() => onSelect(entry.id)}
                data-testid={`screenshot-strip-item-${entry.id}`}
                data-selected={selected ? 'true' : 'false'}
                aria-pressed={selected}
                sx={{
                  flexShrink: 0,
                  width: THUMB_WIDTH,
                  p: 0,
                  borderRadius: '6px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  bgcolor: 'transparent',
                  border: 2,
                  borderColor: selected ? 'primary.main' : 'divider',
                  outline: 'none',
                  '&:focus-visible': { borderColor: 'primary.main' },
                  '&:hover': { borderColor: 'primary.light' },
                }}
              >
                <Box
                  component="img"
                  src={entry.thumbnail_url}
                  alt={screenshotCaption(entry)}
                  sx={{
                    width: '100%',
                    aspectRatio: '16 / 9',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      {hasMore && !showingAll && (
        <Button
          size="small"
          onClick={onShowAll}
          data-testid="screenshot-strip-show-all"
          sx={{ mt: 0.5, textTransform: 'none' }}
        >
          Show all ({total})
        </Button>
      )}
      {hasMore && showingAll && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5 }}
        >
          Showing the {entries.length} most recent of {total} captures
        </Typography>
      )}
    </Box>
  );
};

export default ScreenshotStrip;
